import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { PoChartSerie, PoChartType, PoModalComponent } from '@po-ui/ng-components';

import { Period } from 'src/app/menu/smart-review/core/utils/period.util';
import { withSinglePointTooltip } from 'src/app/menu/smart-review/core/utils/po-chart-tooltip.util';
import { CostBucket } from '../../interfaces/costs.interface';
import {
  costSharePercent,
  costToChartValue,
  currencySymbol,
  formatCostAmount,
  formatTokens,
  roundChartCostValue,
  sumCostStrings
} from '../../utils/costs-format.util';
import { isAggregateTailKey, resolveBucketDisplay } from '../../utils/costs-resource.util';

/** Quantas chaves entram no gráfico. Acima disso as barras ficam finas demais para comparar. */
const MAX_BARS = 10;

/**
 * Uma dimensão do breakdown: barras horizontais com as chaves de maior custo e a
 * tabela completa logo abaixo, recolhida.
 *
 * A barra responde "quem gastou mais" de relance, que é a pergunta frequente; a
 * tabela responde "quanto exatamente", que é ocasional e o gráfico não serve para
 * copiar valor.
 */
@Component({
  selector: 'app-costs-breakdown-section',
  templateUrl: './costs-breakdown-section.component.html'
})
export class CostsBreakdownSectionComponent implements OnChanges {
  /** Rótulo da coluna-chave: "Usuário", "Área", "Review", "Modelo", "Tipo de análise". */
  @Input() keyLabel = 'Chave';

  @Input() rows: CostBucket[] = [];
  @Input() currency = 'USD';

  /** Chaves reais da dimensão, antes do teto de fatias do backend. */
  @Input() totalKeys?: number;

  /** Repassado à tabela: quebra a chave `tipo:id` da dimensão `resource` em duas colunas. */
  @Input() splitResource = false;

  /** Repassado à tabela: recorte da tela, usado no CSV que ela exporta. */
  @Input() period?: Period;

  /** Repassado à tabela: como ler uma coluna desta dimensão. Ver lá. */
  @Input() readingNote = '';

  readonly barType = PoChartType.Bar;

  categories: string[] = [];
  series: PoChartSerie[] = [];

  /**
   * O que a barra mostra quando ela não mostra tudo — ver `buildChartNote`.
   *
   * Propriedade calculada em `ngOnChanges`, não getter: soma o custo de até 200 fatias
   * com `BigInt`, e o template lê a cada ciclo de change detection. Mesmo motivo de
   * `areaRows`/`disclaimers` no painel.
   */
  chartNote = '';

  // `static: false` (não `true`): o modal vive dentro de `*ngIf="hasData"`, uma
  // diretiva estrutural — uma query estática resolve antes do primeiro change
  // detection e nunca voltaria a rodar quando `hasData` virasse true depois.
  @ViewChild('tableModal') tableModal!: PoModalComponent;

  /**
   * A tabela completa abre num `po-modal` — dentro do card ela cortava linha e coluna
   * com o teto de fatias do backend (até 200 linhas, num card de metade da largura da
   * tela). `expanded` só controla o `*ngIf` da tabela dentro do modal — não cria as
   * 200 linhas de DOM até o usuário realmente pedir.
   */
  expanded = false;

  ngOnChanges(): void {
    const top = (this.rows ?? []).slice(0, MAX_BARS);
    this.categories = top.map(bucket => this.displayKey(bucket));
    this.series = top.length
      ? [withSinglePointTooltip({
        label: this.costLabel,
        data: top.map(b => roundChartCostValue(costToChartValue(b.cost)))
      }, this.chartTooltip(top))]
      : [];
    this.chartNote = this.buildChartNote(top);
  }

  /**
   * Diz o que a barra mostra quando ela não mostra tudo — e o quanto disso é o custo do
   * período.
   *
   * A fatia coberta pelas barras é a informação que decide se vale abrir a tabela: se as
   * 10 barras são 85% do período, o resto é ruído; se são 30%, a resposta está fora da
   * tela. Contar chaves ("10 de 332") não diz nada sobre isso — 332 reviews baratos e 332
   * reviews caros dão a mesma contagem.
   */
  private buildChartNote(top: CostBucket[]): string {
    if (!top.length) { return ''; }

    const share = costSharePercent(
      sumCostStrings(top.map(bucket => bucket.cost)),
      sumCostStrings((this.rows ?? []).map(bucket => bucket.cost))
    );

    return `${this.keyLabel === 'Review' ? 'Os' : 'As'} ${this.categories.length} de maior custo, ` +
      `de ${formatTokens(this.keyCount)}` +
      `${share ? ` — ${share} do custo deste recorte` : ''}.`;
  }

  /**
   * Rótulo exibido na barra. Na dimensão `resource` é o mesmo texto que a coluna
   * Review mostra — sem o prefixo de tipo, que já vira tag na tabela — para as duas
   * visões da mesma dimensão lerem igual.
   */
  private displayKey(bucket: CostBucket): string {
    return resolveBucketDisplay(bucket, this.splitResource).key;
  }

  private chartTooltip(top: CostBucket[]): string | undefined {
    return top.length === 1 ? formatCostAmount(top[0].cost) : undefined;
  }

  get hasData(): boolean {
    return this.series.length > 0;
  }

  get costLabel(): string {
    return `Custo (${currencySymbol(this.currency)})`;
  }

  /**
   * Altura acompanha o número de barras, senão elas se espremem.
   *
   * O piso é 160, não 240: 240 sobrava para as dimensões de poucas chaves — "Custo por
   * modelo" tem 1 barra hoje, e o card ficava com mais espaço vazio que gráfico. 36px por
   * barra + 90 de eixo e legenda já é o que uma barra precisa; o piso só protege o caso
   * de uma chave só, em que a conta daria 126 e o rótulo do eixo ficaria colado na
   * legenda.
   */
  get chartHeight(): number {
    return Math.max(160, this.categories.length * 36 + 90);
  }

  /**
   * Chaves da dimensão — o número real, não o que coube nas fatias.
   *
   * No fallback (resposta sem `breakdownKeys`) as linhas são a única fonte, e a cauda
   * `(outros N)` não é uma chave: contá-la faria uma dimensão com teto de 200 anunciar
   * "Ver tabela completa (201)". Mesmo desconto que `buildCappedNote` faz na tabela.
   */
  get keyCount(): number {
    if (this.totalKeys != null) { return this.totalKeys; }

    const rows = this.rows ?? [];
    const hasTail = rows.length > 0 && isAggregateTailKey(rows[rows.length - 1].key);
    return hasTail ? rows.length - 1 : rows.length;
  }

  get isTruncated(): boolean {
    return this.keyCount > this.categories.length;
  }

  get toggleLabel(): string {
    return `Ver tabela completa (${formatTokens(this.keyCount)})`;
  }

  /** Título do modal — mesma coluna-chave que a barra usa, então lê como continuação dela. */
  get modalTitle(): string {
    return `${this.keyLabel} — tabela completa`;
  }

  openTable(): void {
    this.expanded = true;
    this.tableModal.open();
  }

  /** `(p-close)` cobre o X do modal e o clique fora, não só um botão nosso. */
  onTableModalClose(): void {
    this.expanded = false;
  }
}
